import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PhonelinkLockIcon from "@mui/icons-material/PhonelinkLock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { storeSession } from "../authClient";
import { takeAfterLoginPath } from "../auth/deviceLogin";
import { PhoneCodePanel } from "./auth/PhoneCodePanel";
import {
  signInWithPasskey, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, cancelPasskeyPrompt
} from "../auth/passkeys";
import {
  AuthLayout, Eyebrow, Heading, Notice, PrimaryButton, LineButton, GoogleButton,
  StatusCard, LegalLinks, fieldSx, linkSx,
} from "./authShared";

// ─────────────────────────────────────────────────────────────────────────────
// Sign in. A passkey is the way in: no email, no password. Accounts created
// before passkeys still sign in with email + password or Google, behind
// "Older account?", and are offered a passkey once inside.
// ─────────────────────────────────────────────────────────────────────────────

const QUERY_ERRORS = {
  google_no_account:
    "There's no Fennira account for that Google address. New accounts are created with a passkey: choose “Create an account”.",
  session: "Sign-in didn't finish. Please try again."
};

export default function SignIn() {
  const navigate = useNavigate();
  const supported = React.useMemo(() => browserSupportsWebAuthn(), []);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(() => {
    const code = new URLSearchParams(window.location.search).get("error");
    return QUERY_ERRORS[code] || "";
  });
  const [olderOpen, setOlderOpen] = React.useState(!supported);

  // Back to the phone's approval page if that is what sent us here.
  const goHome = React.useCallback(() => navigate(takeAfterLoginPath() || "/home", { replace: true }), [navigate]);
  const [phoneCode, setPhoneCode] = React.useState(false);

  // Passkey autofill: browsers that support it list the person's passkeys in
  // the email field's suggestions, so even someone who opens "Older account"
  // out of habit is offered the passkey first. Cancelled when the page leaves.
  React.useEffect(() => {
    let active = true;
    browserSupportsWebAuthnAutofill().then((ok) => {
      if (!ok || !active) return;
      signInWithPasskey({ autofill: true })
        .then(() => { if (active) goHome(); })
        .catch(() => { /* not chosen, or cancelled by the button below */ });
    }).catch(() => {});
    return () => { active = false; cancelPasskeyPrompt(); };
  }, [goHome]);

  // On a computer only: a phone signs in with its own passkey directly.
  const onComputer = React.useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches, []);

  const passkey = async ({ phone = false } = {}) => {
    setBusy(true);
    setError("");
    try {
      await signInWithPasskey({ phone });
      goHome();
    } catch (err) {
      setBusy(false);
      if (!err.cancelled) setError(err.message);
    }
  };

  return (
    <AuthLayout
      panel={{
        eyebrow: "One source, every platform",
        heading: "Go live everywhere at once.",
        body: "Fennira pushes one encode to YouTube, Facebook, LinkedIn, X, Kick and any RTMP destination, without re-encoding for each one.",
        extra: <StatusCard />,
      }}
    >
      <Eyebrow>Cloud Studio</Eyebrow>
      <Heading>Sign in</Heading>
      <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 16, mb: 4 }}>
        Use the passkey on your phone or computer: your fingerprint, face or screen lock.
      </Typography>

      {error && <Notice onClose={() => setError("")}>{error}</Notice>}
      {!supported && (
        <Notice severity="warning">
          This browser can't use passkeys. Open Fennira in an up-to-date Chrome, Safari, Edge or Firefox.
        </Notice>
      )}

      <PrimaryButton
        onClick={() => passkey()} disabled={busy || !supported}
        startIcon={!busy && <FingerprintIcon />}
      >
        {busy ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-cream)" }} /> : "Sign in with a passkey"}
      </PrimaryButton>

      {/* On a computer: approve it from the phone where Fennira is signed in.
          The code works without Bluetooth; a passkey from a nearby phone (the
          browser's own QR, which needs Bluetooth on both) stays as a link. */}
      {onComputer && !phoneCode && (
        <>
          <LineButton onClick={() => { setError(""); setPhoneCode(true); }} disabled={busy}
            startIcon={<PhonelinkLockIcon />} sx={{ mt: 1.75 }}>
            Sign in with your phone
          </LineButton>
          <Typography sx={{ mt: .75, fontSize: 13.5, color: "var(--fn-muted-l)", textAlign: "center" }}>
            {supported && (
              <Link component="button" type="button" onClick={() => passkey({ phone: true })} disabled={busy} sx={linkSx}>
                Use a passkey from a nearby phone (Bluetooth)
              </Link>
            )}
          </Typography>
        </>
      )}
      {onComputer && phoneCode && (
        <Box sx={{ mt: 1.75, p: { xs: 2, sm: 2.5 }, borderRadius: "18px", border: "1.5px solid var(--fn-line-l)", bgcolor: "var(--fn-white)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: "var(--fn-navy)", mb: 2, textAlign: "center" }}>
            Sign in with your phone
          </Typography>
          <PhoneCodePanel mode="login" tone="light" onApproved={(data) => { storeSession(data); goHome(); }} />
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Link component="button" type="button" onClick={() => setPhoneCode(false)} sx={{ ...linkSx, color: "var(--fn-muted-l)" }}>
              Cancel
            </Link>
          </Box>
        </Box>
      )}

      <LineButton component={RouterLink} to="/signup" disabled={busy} sx={{ mt: 1.75 }}>
        Create an account
      </LineButton>

      <Typography sx={{ mt: 2.5, fontSize: 14.5, color: "var(--fn-muted-l)", textAlign: "center" }}>
        Lost your passkeys?{" "}
        <Link component={RouterLink} to="/recover" sx={linkSx}>Use your recovery code</Link>
      </Typography>

      <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid var(--fn-line-l)" }}>
        <Box
          component="button" type="button" onClick={() => setOlderOpen((v) => !v)}
          aria-expanded={olderOpen} aria-controls="older-signin"
          sx={{
            all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: .75, width: "100%",
            fontSize: 14.5, fontWeight: 600, color: "var(--fn-muted-l)",
            "&:hover, &:focus-visible": { color: "var(--fn-navy)" },
            "&:focus-visible": { outline: "2px solid var(--fn-teal)", outlineOffset: 4, borderRadius: "6px" }
          }}
        >
          Older account? Sign in with email or Google
          <ExpandMoreIcon fontSize="small" sx={{ transition: "transform .2s", transform: olderOpen ? "rotate(180deg)" : "none" }} />
        </Box>
        <Collapse in={olderOpen} id="older-signin">
          <OlderSignIn onDone={goHome} disabled={busy} />
        </Collapse>
      </Box>
      <LegalLinks />
    </AuthLayout>
  );
}

/** Email + password and Google, for accounts created before passkeys. */
function OlderSignIn({ onDone, disabled }) {
  const [showPassword, setShowPassword] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = data.get("email");
    const password = data.get("password");
    if (!email || !password) { setError("Please enter your email and password."); return; }

    setBusy(true);
    setError("");
    try {
      const res = await axios.post("/api/login", { email, password });
      if (!res.data?.token) throw new Error("no token");
      storeSession(res.data);
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || "Sign-in failed. Please check your email and password.");
      setBusy(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit} noValidate sx={{ pt: 2.5 }}>
      {error && <Notice onClose={() => setError("")}>{error}</Notice>}
      <TextField
        fullWidth label="Email address" name="email" type="email"
        // "webauthn" lets the browser offer passkeys here too (autofill).
        autoComplete="username webauthn"
        disabled={busy || disabled} sx={{ ...fieldSx, mb: 2 }}
      />
      <TextField
        fullWidth label="Password" name="password" type={showPassword ? "text" : "password"}
        autoComplete="current-password" disabled={busy || disabled} sx={fieldSx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((v) => !v)} edge="end"
                aria-label={showPassword ? "Hide password" : "Show password"} sx={{ color: "var(--fn-muted-l)" }}>
                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <LineButton type="submit" disabled={busy || disabled} sx={{ mt: 2.5 }}>
        {busy ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-navy)" }} /> : "Sign in with email"}
      </LineButton>
      <GoogleButton onClick={() => { window.location.href = "/auth/google"; }} disabled={busy || disabled} sx={{ mt: 1.5 }} />
    </Box>
  );
}
